// PreviewSection.jsx
// Shows an authentic-looking preview of Section 4 (Karmic Overview) output.
// Content mirrors actual app output language so users know exactly what to expect.
import React from "react";
import { useNavigate } from "react-router-dom";

const DOMAIN_COLORS = {
  career:       { border: "#d4a574", bg: "rgba(212,165,116,0.07)", pill: "#b07d3a", pillBg: "rgba(212,165,116,0.14)" },
  wealth:       { border: "#10b981", bg: "rgba(16,185,129,0.07)",  pill: "#065f46", pillBg: "rgba(16,185,129,0.14)" },
  relationship: { border: "#a855f7", bg: "rgba(168,85,247,0.07)",  pill: "#6b21a8", pillBg: "rgba(168,85,247,0.14)" },
  vitality:     { border: "#38bdf8", bg: "rgba(56,189,248,0.07)",  pill: "#0c4a6e", pillBg: "rgba(56,189,248,0.14)" },
};

export default function PreviewSection({
  heading,
  subheading,
  pattern,
  domains = [],
  cta_label,
  cta_href,
  disclaimer,
}) {
  const navigate = useNavigate();

  const handleCta = () => {
    if (!cta_href) return;
    if (cta_href.startsWith("http") || cta_href.endsWith(".pdf")) {
      window.open(cta_href, "_blank", "noopener noreferrer");
    } else {
      navigate(cta_href);
    }
  };

  return (
    <div className="sx-container">
      <div className="sx-preview-wrapper">
        {heading && <h3 className="sx-pipeline-heading">{heading}</h3>}
        {subheading && <p className="sx-pipeline-sub">{subheading}</p>}

        {/* Overall karmic pattern banner — mirrors actual app output */}
        {pattern && (
          <div className="sx-preview-pattern">
            <span className="sx-preview-pattern-name">
              Current Karmic Pattern — {pattern.name}
            </span>
            <p className="sx-preview-pattern-desc">{pattern.desc}</p>
          </div>
        )}

        {/* Domain card grid */}
        <div className="sx-preview-grid">
          {domains.map((domain, i) => {
            const key = domain.key || "career";
            const colors = DOMAIN_COLORS[key] || DOMAIN_COLORS.career;
            return (
              <div
                key={i}
                className="sx-preview-card"
                style={{
                  borderLeft: `3px solid ${colors.border}`,
                  background: colors.bg,
                }}
              >
                {/* Header row: icon + title + phase pill */}
                <div className="sx-preview-card-header">
                  <span className="sx-preview-card-icon">{domain.icon}</span>
                  <span className="sx-preview-card-title">{domain.title}</span>
                  <span
                    className="sx-preview-card-pill"
                    style={{ color: colors.pill, background: colors.pillBg }}
                  >
                    {domain.phase}
                  </span>
                </div>

                {/* Main insight text */}
                <p className="sx-preview-card-body">{domain.insight}</p>

                {/* Why this pattern */}
                {domain.why && (
                  <div className="sx-preview-card-why">
                    <span className="sx-preview-card-why-label">WHY THIS PATTERN</span>
                    <p className="sx-preview-card-why-text">{domain.why}</p>
                  </div>
                )}

                {/* Calculated metadata — Pressure / Movement / Alignment */}
                {Array.isArray(domain.meta) && domain.meta.length > 0 && (
                  <div className="sx-preview-card-meta">
                    {domain.meta.map((m, j) => (
                      <span key={j} className="sx-preview-meta-item">
                        <span className="sx-preview-meta-key">{m.key}:</span>
                        <span className="sx-preview-meta-val">{m.value}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA */}
        {cta_label && (
          <div className="sx-preview-cta-row">
            <button
              type="button"
              onClick={handleCta}
              className="sx-preview-cta-btn"
            >
              {cta_label}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginLeft: 6 }}>
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {disclaimer && (
              <p className="sx-preview-disclaimer">{disclaimer}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
